import * as React from "react";
import { cn } from "../shared/ui/index.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { PatientAvatar } from "../shared/patient/patient-avatar.component.tsx";
import { PatientNameDisplay } from "../shared/patient/patient-name-display.component.tsx";
import { patientNameParts } from "../shared/patient/patient-name.ts";
import { I } from "../shared/icons/icons.tsx";
import {
  appointmentsForDate,
  appointmentView,
} from "../agenda/appointments.ts";
import { todayLocal, addDays } from "../agenda/date-utils.ts";
import { usePractice } from "../core/providers/practice.provider.tsx";

function hashNum(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function patientBalance(uuid: string): string {
  const h = hashNum(uuid);
  const sign = h % 3 === 0 ? -1 : 1;
  const cents = (h % 24000) * sign;
  return (cents / 100).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });
}

function treatmentStartDate(uuid: string): string {
  const h = hashNum(uuid);
  return new Date(2022 + (h % 3), h % 12, (h % 20) + 1).toLocaleDateString(
    "fr-FR",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    },
  );
}

function InfoRow({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      <span
        className={cn(
          "shrink-0",
          highlight ? "text-sf-primary" : "text-sf-text-muted",
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-[11.5px] text-sf-text-muted">
        {label}
      </span>
      <span
        className={cn(
          "max-w-[56%] truncate text-right text-[12px] font-semibold",
          highlight ? "text-sf-primary" : "text-sf-text",
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function CurrentPatientWidget() {
  const { current } = Interactions.usePatient();
  const { practice } = usePractice();

  if (!current) {
    return (
      <div className="flex h-full min-h-30 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-sf-border bg-sf-surface-alt/60 px-4 py-6 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sf-primary-50 text-sf-primary">
          <I.User size={18} />
        </div>
        <div>
          <p className="text-[12.5px] font-semibold text-sf-text">
            Aucun patient sélectionné
          </p>
          <p className="mt-0.5 max-w-45 text-[11px] leading-relaxed text-sf-text-muted">
            Cliquez sur un patient dans « Patients du jour »
          </p>
        </div>
      </div>
    );
  }

  const p = current;
  const { first, last } = patientNameParts(p.name);
  const email = `${(first || "patient").toLowerCase()}.${(last || "x").toLowerCase()}@email.fr`;
  const balance = patientBalance(p.uuid);
  const balanceNeg = balance.startsWith("-");

  const today = todayLocal();
  const todayAppts = appointmentsForDate(today, practice.uuid);
  const nextAppt = todayAppts.find((a) => a.patientUuid === p.uuid);
  const tomorrowAppts = appointmentsForDate(addDays(today, 1), practice.uuid);
  const nextApptTomorrow = tomorrowAppts.find((a) => a.patientUuid === p.uuid);
  const nextApptView = nextAppt
    ? appointmentView(nextAppt)
    : nextApptTomorrow
      ? appointmentView(nextApptTomorrow)
      : null;
  const nextApptLabel = nextAppt
    ? `Auj. — ${String(Math.floor(nextApptView!.start)).padStart(2, "0")}h${String(Math.round((nextApptView!.start % 1) * 60)).padStart(2, "0")}`
    : nextApptTomorrow
      ? `Dem. — ${String(Math.floor(nextApptView!.start)).padStart(2, "0")}h${String(Math.round((nextApptView!.start % 1) * 60)).padStart(2, "0")}`
      : "Aucun RDV proche";

  return (
    <div className="flex flex-col gap-2">
      {/* Header */}
      <div className="overflow-hidden rounded-xl border border-sf-primary-100/70 shadow-(--sf-shadow-sm)">
        <div className="bg-linear-to-br from-sf-primary-50/90 to-white p-3">
          <div className="flex items-start gap-3">
            <PatientAvatar
              patient={p}
              size={46}
              ring
              online
              className="shrink-0"
            />
            <div className="min-w-0 flex-1">
              <PatientNameDisplay
                name={p.name}
                firstClassName="text-[14.5px] font-bold leading-tight tracking-tight"
              />
              <p className="mt-0.5 text-[11px] text-sf-text-secondary">
                {p.age} ans · {p.sex === "F" ? "Féminin" : "Masculin"}
              </p>
              <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-sf-primary/10 px-2 py-0.5 text-[10.5px] font-semibold text-sf-primary">
                <I.Activity size={9} className="shrink-0" />
                <span className="truncate">{p.tag}</span>
              </span>
            </div>
            <div
              className={cn(
                "shrink-0 rounded-lg px-2 py-1.5 text-right",
                balanceNeg ? "bg-red-50" : "bg-emerald-50",
              )}
            >
              <div
                className={cn(
                  "text-[13px] font-bold tabular-nums leading-tight",
                  balanceNeg ? "text-red-600" : "text-emerald-600",
                )}
              >
                {balance}
              </div>
              <div className="text-[9.5px] text-sf-text-muted">solde</div>
            </div>
          </div>
        </div>
      </div>

      {/* Info rows */}
      <div className="overflow-hidden rounded-xl border border-sf-border bg-sf-surface">
        <InfoRow
          icon={<I.Phone size={12} />}
          label="Téléphone"
          value={p.phone}
        />
        <div className="mx-3 h-px bg-sf-border" />
        <InfoRow icon={<I.Message size={12} />} label="Email" value={email} />
        <div className="mx-3 h-px bg-sf-border" />
        <InfoRow
          icon={<I.Calendar size={12} />}
          label="Début traitement"
          value={treatmentStartDate(p.uuid)}
        />
        <div className="mx-3 h-px bg-sf-border" />
        <InfoRow
          icon={<I.Clock size={12} />}
          label="Dernière visite"
          value={p.last}
        />
        <div className="mx-3 h-px bg-sf-border" />
        <InfoRow
          icon={<I.CalendarDay size={12} />}
          label="Prochain RDV"
          value={nextApptLabel}
          highlight={!!nextAppt}
        />
        <div className="mx-3 h-px bg-sf-border" />
        <InfoRow
          icon={<I.Stethoscope size={12} />}
          label="Traitement"
          value="Multi-attaches Cl. II"
        />
      </div>
    </div>
  );
}
